import { AlertTriangle, Check, Globe, Info, Plus, Shield, X } from "lucide-react";
import { useEffect, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { useTranslations } from "@/shared/lang/useTranslations";

import { apiKeysService, type AllowedIp } from "../services/apiKeysService";

interface EditApiKeyIpsDialogProps {
  open: boolean;
  apiKeyName: string;
  apiKeyPrefix?: string;
  initialIps: AllowedIp[];
  isAdmin?: boolean;
  onSave: (ips: AllowedIp[]) => Promise<void>;
  onCancel: () => void;
}

const IPV4_OR_CIDR = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/;
const IPV6_FULL = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
const IPV6_COMPRESSED = /^(([0-9a-fA-F]{1,4}:){0,7}|::)([0-9a-fA-F]{1,4}(:[0-9a-fA-F]{1,4}){0,6})?$/;

function isValidIp(value: string): boolean {
  const trimmed = value.trim();
  const m = trimmed.match(IPV4_OR_CIDR);
  if (m) {
    if ([1, 2, 3, 4].some((i) => Number(m[i]) > 255)) return false;
    if (m[5] && Number(m[5]) > 32) return false;
    return true;
  }
  if (!trimmed.includes(":")) return false;
  if (IPV6_FULL.test(trimmed)) return true;
  return trimmed.includes("::") && IPV6_COMPRESSED.test(trimmed);
}

export function EditApiKeyIpsDialog({
  open,
  apiKeyName,
  apiKeyPrefix,
  initialIps,
  isAdmin,
  onSave,
  onCancel,
}: EditApiKeyIpsDialogProps) {
  const { t } = useTranslations("settings");

  const [ips, setIps] = useState<AllowedIp[]>(initialIps);
  const [newIp, setNewIp] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [myIp, setMyIp] = useState<string | null>(null);
  const [isDetectingIp, setIsDetectingIp] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIps(initialIps);
    setNewIp("");
    setNewLabel("");
    setError(null);
    setMyIp(null);
    setIsDetectingIp(true);
    let cancelled = false;
    apiKeysService
      .getCurrentIp()
      .then((ip) => {
        if (cancelled) return;
        setMyIp(ip && ip !== "unknown" ? ip : null);
      })
      .finally(() => {
        if (!cancelled) setIsDetectingIp(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, initialIps]);

  const handleAdd = () => {
    const ip = newIp.trim();
    const label = newLabel.trim();
    if (!ip) return;
    if (!isValidIp(ip)) {
      setError(t("api_keys_invalid_ip"));
      return;
    }
    if (ips.some((x) => x.ip === ip)) {
      setError(t("api_keys_ip_duplicate"));
      return;
    }
    setIps([...ips, { ip, ...(label ? { label } : {}) }]);
    setNewIp("");
    setNewLabel("");
    setError(null);
  };

  const handleRemove = (ip: string) => {
    setIps(ips.filter((x) => x.ip !== ip));
  };

  const handleAddMyIp = () => {
    if (!myIp || ips.some((x) => x.ip === myIp)) return;
    setIps([...ips, { ip: myIp, label: t("api_keys_my_ip_label_default") }]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(ips);
    } finally {
      setIsSaving(false);
    }
  };

  const myIpAlreadyAdded = !!myIp && ips.some((x) => x.ip === myIp);

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={
        <span className="flex items-center gap-2">
          <Shield size={16} className="text-primary" />
          {t("api_keys_edit_ips_title")}
        </span>
      }
      description={
        <span>
          {apiKeyName}
          {apiKeyPrefix && (
            <span className="ml-2 font-mono text-[11px] text-muted-foreground">· {apiKeyPrefix}</span>
          )}
        </span>
      }
      size="default"
      footer={
        <>
          <ColocaDialogClose
            render={
              <ColocaButton variant="ghost" disabled={isSaving}>
                {t("api_keys_edit_ips_cancel")}
              </ColocaButton>
            }
          />
          <ColocaButton variant="primary" icon={Check} onClick={handleSave} loading={isSaving}>
            {t("api_keys_edit_ips_save")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          {t("api_keys_edit_ips_description")}{" "}
          <span className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
            403 Forbidden
          </span>
          .
        </p>

        {/* Admin warning */}
        {isAdmin && ips.length === 0 && (
          <div className="flex items-start gap-2 rounded-sm border border-warning/40 bg-warning/10 px-3 py-2.5 text-[12px]">
            <AlertTriangle size={13} className="mt-0.5 shrink-0 text-warning" />
            <span>{t("api_keys_admin_no_ip_warning")}</span>
          </div>
        )}

        {/* IP list header */}
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
          <span>{t("api_keys_edit_ips_list_label")}</span>
          <span className="rounded-full border border-border bg-muted px-1.5 py-px text-[9.5px]">
            {ips.length}
          </span>
          <span className="ml-auto font-sans text-[11px] normal-case tracking-normal text-muted-foreground">
            {ips.length === 0
              ? t("api_keys_any_ip_pill")
              : `${ips.length} ${ips.length === 1 ? t("api_keys_ip_entry_singular") : t("api_keys_ip_entry_plural")}`}
          </span>
        </div>

        {ips.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 rounded-sm border border-dashed border-border bg-muted/30 px-3 py-5 text-center text-[12px] text-muted-foreground">
            <Globe size={16} />
            {t("api_keys_no_ips_hint")}
          </div>
        ) : (
          <div className="overflow-hidden rounded-sm border border-border">
            {ips.map((entry, i) => (
              <div
                key={entry.ip}
                className={
                  "grid grid-cols-[auto_1fr_auto] items-center gap-2.5 px-3 py-2.5 " +
                  (i === ips.length - 1 ? "" : "border-b border-border ") +
                  (i % 2 ? "bg-card" : "bg-muted/30")
                }
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-mono text-[12.5px] font-semibold tracking-[0.03em] text-foreground">
                    {entry.ip}
                    {entry.ip.includes("/") && (
                      <span className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                        CIDR
                      </span>
                    )}
                  </div>
                  {entry.label && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{entry.label}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(entry.ip)}
                  aria-label={t("api_keys_edit_ips_remove")}
                  className="grid h-7 w-7 place-items-center rounded text-destructive hover:bg-destructive/10"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new IP */}
        <div>
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {t("api_keys_add_ip_label")}
          </div>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-1.5">
            <ColocaInput
              value={newIp}
              onValueChange={(v) => {
                setNewIp(v);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder={t("api_keys_edit_ips_placeholder")}
              aria-invalid={!!error}
              className="font-mono"
            />
            <ColocaInput
              value={newLabel}
              onValueChange={setNewLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder={t("api_keys_ip_label_placeholder")}
            />
            <ColocaButton variant="primary" icon={Plus} onClick={handleAdd}>
              {t("api_keys_edit_ips_add")}
            </ColocaButton>
          </div>
          {error && (
            <p className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-destructive">
              <AlertTriangle size={12} /> {error}
            </p>
          )}
        </div>

        {/* My IP hint */}
        <div className="flex items-center gap-2 rounded-sm border border-dashed border-border bg-muted/30 px-3 py-2 text-[11.5px] text-muted-foreground">
          <Info size={12} />
          {isDetectingIp ? (
            <span>{t("api_keys_my_ip_detecting")}</span>
          ) : myIp ? (
            <>
              <span>
                {t("api_keys_your_current_ip_is")}{" "}
                <span className="font-mono font-semibold text-foreground">{myIp}</span>
              </span>
              {myIpAlreadyAdded ? (
                <span className="ml-auto flex items-center gap-1 text-primary">
                  <Check size={11} /> {t("api_keys_ip_added")}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleAddMyIp}
                  className="ml-auto inline-flex h-6 items-center gap-1 rounded px-2 text-[11.5px] text-foreground hover:bg-foreground/5"
                >
                  <Plus size={11} /> {t("api_keys_add_my_ip")}
                </button>
              )}
            </>
          ) : (
            <span>{t("api_keys_my_ip_unavailable")}</span>
          )}
        </div>
      </div>
    </ColocaDialog>
  );
}
